import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { client } from "@johnpaul/jp-api-client";

export const useRequests = (enabled = true) => {
  return useQuery({
    queryKey: ["my-requests"],
    queryFn: () => client.requests.v3.fetchAll("me", { limit: 200 }),
    enabled,
    retry: false,
    gcTime: 0,
  });
};

export const useRequest = (requestId: string, enabled = true) => {
  return useQuery({
    queryKey: ["request", requestId],
    queryFn: () => client.requests.v3.fetch("me", requestId),
    enabled,
    retry: false,
    gcTime: 0,
  });
};

export const useProposals = (requestId: string, enabled = true) => {
  return useQuery({
    queryKey: ["proposals", requestId],
    queryFn: () => client.requests.v3.proposal.fetch("me", requestId),
    enabled,
    retry: false,
    gcTime: 0,
  });
};

type RejectRecommendationsVariables = {
  requestId: string;
  proposalId: string;
  payload: Record<string, unknown>;
};

export const useRejectRecommendations = () => {
  const mutationFn: MutationFunction<unknown, RejectRecommendationsVariables> = async ({
    requestId,
    proposalId,
    payload,
  }) => {
    return await client.requests.v3.proposal.reject("me", requestId, proposalId, payload);
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
  });
};

export const useRecommendation = (
  requestId: string,
  proposalId: string,
  recommendationId: string
) => {
  return useQuery({
    queryKey: ["recommendation"],
    queryFn: () =>
      client.requests.v3.recommendation.fetch("me", requestId, proposalId, recommendationId),
    gcTime: 0,
    retry: false,
  });
};

interface ModifyRequestProps {
  id: string;
  family: string;
  payload: { comment: string };
}

export const useModifyRequest = () =>
  useMutation({
    mutationFn: async ({ id, payload }: ModifyRequestProps) =>
      await client.requests.v3.comment("me", id, payload),
    retry: false,
    gcTime: 0,
  });

interface CancelRequestProps {
  id: string;
  family: string;
}

export const useCancelRequest = () =>
  useMutation({
    mutationFn: async ({ id }: CancelRequestProps) => await client.requests.v3.cancel("me", id, {}),
    retry: false,
    gcTime: 0,
  });

type StateModificationRecommendationVariables = {
  requestId: string;
  proposalId: string;
  recommendationId: string;
};

export const useAcceptRecommendation = () => {
  const mutationFn: MutationFunction<unknown, StateModificationRecommendationVariables> = async ({
    requestId,
    proposalId,
    recommendationId,
  }) => {
    return await client.requests.v3.recommendation.accept(
      "me",
      requestId,
      proposalId,
      recommendationId
    );
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
  });
};

type BellCreateRequestVariables = {
  payload: Record<string, unknown>;
};

export const useCreateRequest = () =>
  useMutation({
    mutationFn: async ({ payload }: BellCreateRequestVariables) =>
      await client.requests.v3.create("me", payload),
    retry: false,
    gcTime: 0,
  });
